import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { ListTree } from "lucide-react";

const getProductCategory = (product) => product.dzi_category || "other";

export default function BulkEyeCategoryDialog({
    open,
    onOpenChange,
    products = [],
    dziOptions = [],
    onDone,
}) {
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setValues(Object.fromEntries(
            products.map((product) => [product.id, getProductCategory(product)]),
        ));
    }, [open, products]);

    const changes = useMemo(() => products.filter((product) => (
        (values[product.id] || "other") !== getProductCategory(product)
    )), [products, values]);

    const save = async () => {
        if (changes.length === 0) {
            toast.info("尚未修改任何商品的眼数分类");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/products/bulk-eye-categories", {
                items: changes.map((product) => ({
                    product_id: product.id,
                    dzi_category: values[product.id] || "other",
                })),
            });
            toast.success(`已更新 ${data.updated} 件商品的眼数分类`);
            onOpenChange(false);
            onDone?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "眼数分类保存失败");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[90vh] overflow-y-auto"
                data-testid="bulk-eye-category-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ListTree className="h-4 w-4 text-gold-600" />
                        批量整理眼数分类
                    </DialogTitle>
                    <DialogDescription className="pt-1 text-xs text-gold-600">
                        每件商品独立选择眼数；只会保存实际变动的项目。
                    </DialogDescription>
                </DialogHeader>

                <div className="overflow-hidden rounded-md border border-slate-200">
                    <div className="hidden grid-cols-[minmax(0,1fr)_7rem_10rem] gap-3 bg-gold-50 px-4 py-2 text-xs font-semibold text-gold-700 sm:grid">
                        <span>商品</span>
                        <span>SKU</span>
                        <span>眼数分类</span>
                    </div>
                    <div className="max-h-[52vh] divide-y divide-slate-100 overflow-y-auto">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                className="grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-3 px-4 py-2.5 sm:grid-cols-[minmax(0,1fr)_7rem_10rem]"
                                data-testid={`bulk-eye-row-${product.id}`}
                            >
                                <span className="truncate text-sm font-medium text-slate-900">
                                    {product.name}
                                    <span className="mt-0.5 block truncate font-mono text-[10px] font-normal text-slate-500 sm:hidden">
                                        {product.sku}
                                    </span>
                                </span>
                                <span className="hidden truncate font-mono text-xs text-slate-500 sm:block">
                                    {product.sku}
                                </span>
                                <Select
                                    value={values[product.id] || "other"}
                                    onValueChange={(value) => setValues((current) => ({
                                        ...current,
                                        [product.id]: value,
                                    }))}
                                >
                                    <SelectTrigger
                                        data-testid={`bulk-eye-select-${product.id}`}
                                        className="h-9 text-xs"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {dziOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>
                </div>

                <DialogFooter className="items-center gap-2 pt-2">
                    <span
                        className="mr-auto text-xs text-gold-600"
                        data-testid="bulk-eye-change-count"
                    >
                        已修改 {changes.length} / {products.length} 件
                    </span>
                    <Button
                        type="button"
                        variant="outline"
                        data-testid="bulk-eye-cancel-button"
                        onClick={() => onOpenChange(false)}
                    >
                        取消
                    </Button>
                    <Button
                        type="button"
                        data-testid="bulk-eye-save-button"
                        disabled={saving || changes.length === 0}
                        onClick={save}
                        className="bg-gold-600 text-white hover:bg-gold-700"
                    >
                        {saving ? "保存中..." : `保存 ${changes.length} 项变动`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}